import React, { useState, useContext, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { blue, purple, green, yellow } from "@material-ui/core/colors";

import { AuthContext } from "../contexts/AuthContext";
import { AdminContext } from "../contexts/AdminContext";
import { ScreenContext } from "../contexts/ScreenContext";
import { HoursContext } from "../contexts/HoursContext";
import { ClientContext } from "../contexts/ClientContext";

import clsx from "clsx";

import Collapse from "@material-ui/core/Collapse";
import LinearProgress from "@material-ui/core/LinearProgress";
import Fade from "@material-ui/core/Fade";

import Button from "@material-ui/core/Button";
import Icon from "@material-ui/core/Icon";
import UpdateIcon from "@material-ui/icons/Update";
import AddBoxIcon from "@material-ui/icons/AddBox";

import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";

import HoursCalendar from "../components/HoursCalendar";
import InlineCalendar from "../components/InlineCalendar";
import HoursList from "../components/HoursList";
import HoursFrom from "../components/HoursForm";
import TogglQuotaStatus from "../components/TogglQuotaStatus";
import { Typography } from "@material-ui/core";

const ManageHours = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { getDailyMessage, dailyMessage } = useContext(AdminContext);
  const { screenSize, setSnackbarMessage } = useContext(ScreenContext);
  const { loadClients, setSelectedClient } = useContext(ClientContext);
  const {
    date,
    changeDate,
    loadHours,
    hours,
    totalHours,
    deleteHour,
    createTicket,
    emailEntries,
    toggl,
    selectedDays,
    setJobDetails,
    isLoading,
    saveHour,
  } = useContext(HoursContext);

  const [showForm, setShowForm] = useState(false);
  const [formAction, setFormAction] = useState("New");
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogText, setDialogText] = useState(null);
  const [loadedDailyMessage, setloadedDailyMessage] = useState([]);
  // set anchor for menu list in HoursList
  const [anchorEl, setAnchorEl] = useState(null);

  let dialogTextInfo = {
    selectedItem: {},
    ticket: {
      title: "Send hour information to ZOHO Ticket System?",
      body: (
        <React.Fragment>
          By clicking on <b>"Yes, Send Ticket"</b> a ticket will be created for
          this hour and will be assigned to [<b>{user.username}</b>].
        </React.Fragment>
      ),
      confirmText: "Yes, Send Ticket",
      declineText: "CANCEL",
      action: () => {
        createTicket(dialogTextInfo.selectedItem);
      },
    },
    delete: {
      title: "Delete Hour Entry",
      body: (
        <React.Fragment>
          By clicking on <b>"Yes, Delete Hour Entry"</b> your hour entry will be
          deleted permanently.
        </React.Fragment>
      ),
      confirmText: "Yes, Delete Hour Entry",
      declineText: "CANCEL",
      action: () => {
        deleteHour(dialogTextInfo.selectedItem);
        handleNewEntry();
      },
    },
  };

  const handleItemMenuClick = (event, item, action = null) => {
    dialogTextInfo.selectedItem = item;

    if (!action) setAnchorEl(event.currentTarget);
    if (action === "EDIT") handleMenuEdit(item);
    if (action === "SEND_TICKET") handleMenuSendTicket(item);
    if (action === "DELETE") handleMenuDelete(item);

    if (action === "MAKE_ADMIN") handleMenuChangeToAdmin(item);
    if (action === "MAKE_INDIRECT") handleMenuChangeToInDirect(item);
  };

  const handleMenuChangeToAdmin = (item) => {
    let newHour = item;
    newHour.FormAction = "Update";
    newHour.Type = item.DorI;
    newHour.CombinedID = `${item.ID}|${item.Completed}|${item.StartTime}|${user.username}`;

    saveHour(newHour);
  };

  const handleMenuChangeToInDirect = (item) => {
    let newHour = item;
    newHour.FormAction = "Update";
    newHour.Type = "InDirect";
    newHour.CombinedID = `${item.ID}|${item.Completed}|${item.StartTime}|${user.username}`;

    saveHour(newHour);
  };

  const handleMenuEdit = (item) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });

    let client = [
      {
        ID: item.ID,
        Name: item.Name,
      },
    ];

    setSelectedClient(client);
    setJobDetails(item);
    setShowForm(true);
    setFormAction("Update");
    handleMenuClose();
  };

  const handleMenuDelete = () => {
    setDialogText(dialogTextInfo.delete);
    setOpenDialog(true);
  };

  const handleMenuSendTicket = () => {
    setDialogText(dialogTextInfo.ticket);
    setOpenDialog(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDialogResponse = (response, action) => {
    if (response) action();
    handleCloseDialog();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    handleMenuClose();
  };

  const handleNewEntry = () => {
    setJobDetails({ key: Math.random() }); // { key: Math.random() } forces JobDetails state to refresh
    setSelectedClient(null);
    setShowForm(true);
    setFormAction("New");
  };

  const handleEmailDateEntries = () => {
    if (hours.length > 0) {
      emailEntries(date);
    } else {
      setSnackbarMessage({
        message: "0 hours to email!",
        severity: "error",
      });
    }
  };

  const handleToggle = () => {
    toggl(date);
  };

  // Optimized dialog handlers to prevent re-renders
  const handleDialogConfirm = useCallback(() => {
    if (dialogText && dialogText.action) {
      handleDialogResponse(true, dialogText.action);
    }
  }, [dialogText]);

  const handleDialogDecline = useCallback(() => {
    if (dialogText && dialogText.action) {
      handleDialogResponse(false, dialogText.action);
    }
  }, [dialogText]);

  useEffect(() => {
    if (dailyMessage.length) {
      setloadedDailyMessage(dailyMessage);
    }
  }, [dailyMessage]);

  useEffect(() => {
    let mounted = true;

    if (mounted) loadHours();

    return () => {
      mounted = false;
    };
  }, [date]); // Only depend on date changes, not the loadHours function

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      loadClients();
      getDailyMessage();
    }

    return () => {
      mounted = false;
    };
  }, []); // Empty dependency array - only run on mount

  useEffect(() => {
    let mounted = true;

    if (screenSize === "lg" || screenSize === "xl" || formAction == "Update") {
      if (mounted) setShowForm(true);
    } else {
      if (mounted) setShowForm(false);
    }

    return () => {
      mounted = false;
    };
  }, [screenSize]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        direction="row"
        alignItems="stretch"
        alignContent="stretch"
      >
        <Grid item lg={"auto"} xs={12}>
          {loadedDailyMessage.length > 0 &&
            loadedDailyMessage[0].value != "" && (
              <Box className={classes.messageBox}>
                <Paper
                  className={clsx(classes.messageBoxPaper)}
                  elevation={0}
                  variant="outlined"
                >
                  <Typography variant="button" display="block">
                    NOTICE:
                  </Typography>
                  <Box style={{ maxWidth: 280 }}>
                    <Typography
                      variant="body2"
                      display="block"
                    >{`${loadedDailyMessage[0].value}`}</Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    display="block"
                    align="right"
                  >{`-- ${loadedDailyMessage[0].first_name} ${loadedDailyMessage[0].last_name}`}</Typography>
                </Paper>
              </Box>
            )}

          <Grid container direction="row" className={classes.topButtons}>
            <Grid item>
              <Button
                fullWidth
                variant="outlined"
                size="medium"
                className={clsx(classes.button, classes.email)}
                startIcon={<Icon>send</Icon>}
                onClick={handleEmailDateEntries}
              >
                Email
              </Button>
            </Grid>
            <Grid item>
              <Button
                fullWidth
                variant="outlined"
                size="medium"
                className={clsx(classes.button, classes.toggl)}
                startIcon={<UpdateIcon />}
                onClick={handleToggle}
              >
                Toggl
              </Button>
            </Grid>
            <Grid item>
              <Button
                fullWidth
                variant="outlined"
                size="medium"
                className={clsx(classes.button, classes.addNew)}
                startIcon={<AddBoxIcon />}
                onClick={handleNewEntry}
              >
                Add New
              </Button>
            </Grid>
          </Grid>

          <Paper
            className={clsx(classes.paper, classes.noPadding)}
            elevation={0}
            variant="outlined"
            square
          >
            <Box style={{ padding: 10 }} display={{ md: "block", lg: "none" }}>
              <InlineCalendar
                date={date}
                changeDate={changeDate}
                selectedDays={selectedDays}
              />
            </Box>

            <Box display={{ xs: "none", md: "none", lg: "block" }}>
              <HoursCalendar
                date={date}
                changeDate={changeDate}
                selectedDays={selectedDays}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item style={{ flexGrow: 1 }} md={"auto"} xs={12}>
          <Collapse in={showForm}>
            <HoursFrom
              formAction={formAction}
              setShowForm={setShowForm}
              setFormAction={setFormAction}
            />
          </Collapse>
        </Grid>
      </Grid>

      {/* Toggl Quota Status */}
      <TogglQuotaStatus />

      <Grid container spacing={3}>
        <Grid item style={{ flexGrow: 1 }}>
          {isLoading && (
            <Fade in={true}>
              <LinearProgress style={{ marginBottom: 5 }} />
            </Fade>
          )}

          <HoursList
            date={date}
            hours={hours}
            totalHours={totalHours}
            setSelectedClient={setSelectedClient}
            setJobDetails={setJobDetails}
            handleItemMenuClick={handleItemMenuClick}
            screenSize={screenSize}
          />
        </Grid>
      </Grid>

      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuEdit}>Edit</MenuItem>
        <MenuItem onClick={handleMenuSendTicket}>Send Ticket</MenuItem>
        <MenuItem onClick={handleMenuDelete} className={classes.delete}>
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        disableEnforceFocus={true}
      >
        <DialogTitle id="alert-dialog-title">
          {dialogText ? dialogText.title : ""}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogText ? dialogText.body : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDialogConfirm}
            color="primary"
          >
            {dialogText ? dialogText.confirmText : ""}
          </Button>
          <Button
            onClick={handleDialogDecline}
            color="primary"
            autoFocus
          >
            {dialogText ? dialogText.declineText : ""}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ManageHours;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "left",
    color: theme.palette.text.secondary,
  },
  noPadding: {
    padding: 0,
  },
  messageBoxPaper: {
    padding: theme.spacing(1),
    textAlign: "left",
    color: theme.palette.text.secondary,
    backgroundColor: yellow[100],
  },
  messageBox: {
    marginBottom: theme.spacing(1),
  },
  button: {
    marginBottom: theme.spacing(1),
    // marginLeft: theme.spacing(1),
  },
  email: {
    color: theme.palette.getContrastText(blue[500]),
    backgroundColor: blue[500],
    "&:hover": {
      backgroundColor: blue[700],
    },
    borderColor: blue[700],
  },
  toggl: {
    color: theme.palette.getContrastText(purple[500]),
    backgroundColor: purple[500],
    "&:hover": {
      backgroundColor: purple[700],
    },
    borderColor: purple[700],
  },
  addNew: {
    color: theme.palette.getContrastText(green[700]),
    backgroundColor: green[500],
    "&:hover": {
      backgroundColor: green[700],
    },
    borderColor: green[700],
  },
  topButtons: {
    justifyContent: "space-between",
  },
  formAction: {
    direction: "row",
    justifyContent: "flex-start",
    paddingTop: 15,
  },
  delete: {
    color: theme.palette.primary.main,
  },
}));
